"use client";

import callAPIFromClient from "@/app/api/api";
import {
    EVENT_NAME,
    EVENT_CATEGORY,
    EVENT_ACTION,
} from "@/constants/constants";
import { UserDetails } from "@/model/global/shape";
import { trackEvent } from "@/utils/analytics";
import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";

interface UserContextType {
    user: UserDetails | null;
    setUser: (user: UserDetails | null) => void;
    handleLogout: (options?: {
        isMobile?: boolean;
        skipLogoutApi?: boolean;
    }) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<UserDetails | null>(null);

    const handleLogout = async (
        {
            retriggerAuth = false,
            skipLogoutApi = false,
        }: { retriggerAuth?: boolean; skipLogoutApi?: boolean } = {
            retriggerAuth: false,
            skipLogoutApi: false,
        },
    ) => {
        const response = await fetch("/api/auth/get-auth-token");
        const data = await response.json();
        const authToken = data.authToken;

        if (authToken === undefined) {
            localStorage.removeItem("user_details");
            setUser(null);
        } else {
            try {
                const response = await callAPIFromClient({
                    url: "/api/auth/logout",
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: { skipLogoutApi },
                });

                if (response.statusCode === 200) {
                    //@ts-ignore
                    if (window.DM_CHANNEL) {
                        // @ts-ignore
                        window.DM_CHANNEL.postMessage("LOGOUT_EVENT");
                    }

                    localStorage.removeItem("auth_token");
                    localStorage.removeItem("user_details");
                    setUser(null); // Clear user context

                    trackEvent({
                        event_name: EVENT_NAME.LOGOUT,
                        category: EVENT_CATEGORY.AUTHENTICATION,
                        action: EVENT_ACTION.USER_LOGOUT,
                        label: "User Logout",
                    });

                    if (retriggerAuth) {
                        if (window.location.search) {
                            window.location.href =
                                window.location.pathname +
                                window.location.search +
                                "&retriggerAuth=true";
                        } else {
                            window.location.href =
                                window.location.pathname +
                                window.location.search +
                                "?retriggerAuth=true";
                        }
                    } else {
                        window.location.href =
                            window.location.pathname + window.location.search;
                    }
                } else {
                    console.error("Logout failed");
                }
            } catch (error) {
                console.error("Error during logout:", error);
            }
        }
    };

    // Load user from localStorage on mount
    useEffect(() => {
        const storedUser = localStorage.getItem("user_details");
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    // Save user to localStorage when updated
    useEffect(() => {
        if (user) {
            localStorage.setItem("user_details", JSON.stringify(user));
        } else {
            localStorage.removeItem("user_details");
        }
    }, [user]);

    return (
        <UserContext.Provider value={{ user, setUser, handleLogout }}>
            {children}
        </UserContext.Provider>
    );
};

// Custom hook to access user context
export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUserContext must be used within a UserProvider");
    }
    return context;
};
